import { BRAND } from "@/lib/site";
import SEO from "@/components/SEO";

const PORTRAIT = "/images/brandon-portrait.png";

const PHILOSOPHY = [
  {
    title: "Open Mobility",
    body: "Improve access to useful, functional ranges of motion.",
  },
  {
    title: "Create Stability",
    body: "Develop the control, coordination, and strength needed to own those ranges.",
  },
  {
    title: "Build Capacity",
    body: "Gradually increase load, volume, speed, and complexity so the body can perform and adapt safely.",
  },
];

const audiences = [
  "Athletes wanting to move faster and stay durable",
  "Lifters chasing better positions and joint health",
  "Active adults who feel stiff, tight, or restricted",
  "People rebuilding after long desk hours",
  "Anyone who wants recovery, mobility, and confidence",
];

export default function About() {
  return (
    <div>
      <SEO
        title="About Brandon Williams"
        path="/about"
        description="Meet Brandon Williams — Kinesiology graduate, Mobility Specialist, and Personal Trainer in Mississauga / GTA. Founder of BW MATRIX."
      />
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-28">
              <div className="overflow-hidden border border-white/10">
                <img src={PORTRAIT} alt="Brandon Williams" className="w-full h-[520px] object-cover object-[center_30%]" />
              </div>
              <p className="mt-6 label-eyebrow">{BRAND.location}</p>
              <p className="mt-2 text-sm text-zinc-400">In person & online coaching</p>
            </div>
          </div>
          <div className="md:col-span-7 space-y-12">
            <div>
              <p className="label-eyebrow mb-4">My Story</p>
              <p className="text-xl md:text-2xl text-white leading-snug font-display uppercase tracking-tight">
                I'm Brandon Williams, a Personal Trainer, Certified Mobility Specialist and Fascia Stretch Therapist.
              </p>
            </div>

            <div className="space-y-6 text-base md:text-lg text-zinc-300 leading-relaxed">
              <p>
                My own experiences with sports, injuries, surgeries and rebuilding my body have had a major
                influence on the way I coach. I know what it feels like to lose confidence in how your body
                moves and to work your way back toward feeling strong, athletic, and capable again. That
                process pushed me to learn more about movement, mobility, strength, joint health, and
                long-term physical performance.
              </p>
              <p>
                Today, I use that experience alongside my education and training to help people move
                better, get stronger, improve their mobility, and feel more confident in what their body
                can do.
              </p>
              <p>
                Whether you're an athlete trying to perform at a higher level, a lifter who feels
                restricted, or someone who simply wants to move and feel better, my goal is to meet you
                where you are and build a plan around what your body actually needs.
              </p>
              <p className="text-white">
                I want to help you develop a body that doesn't just perform better now, but has the
                strength, mobility, control, and resilience to keep doing the things you love for years to
                come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BW's Philosophy */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-5">
              <h2 className="font-display uppercase font-bold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
                BW's<br />
                <span className="text-white/50">Philosophy.</span>
              </h2>
            </div>
            <div className="md:col-span-7">
              <p className="text-base md:text-lg text-zinc-300 leading-relaxed">
                Every plan I build follows a simple, ordered framework: create room in the joints, teach the
                body to own that room, then load it until it becomes durable, transferrable strength.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PHILOSOPHY.map((p, i) => (
              <li
                key={p.title}
                data-testid={`philosophy-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="border border-white/10 bg-[#141417] p-8 md:p-10 flex flex-col"
              >
                <p className="font-display text-white/30 text-lg font-bold">0{i + 1}</p>
                <h3 className="mt-4 font-display uppercase text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-4 text-zinc-400 leading-relaxed text-sm md:text-base">
                  {p.body}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Who I help */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="label-eyebrow mb-4">Who I help</p>
            <h2 className="font-display uppercase font-bold text-3xl md:text-5xl leading-[0.95] tracking-tight text-white">
              Athletes.<br />
              Lifters.<br />
              <span className="text-white/50">People rebuilding.</span>
            </h2>
          </div>
          <ul className="md:col-span-8 space-y-4 border-t border-white/10">
            {audiences.map((a, i) => (
              <li key={i} className="pt-4 border-b border-white/10 pb-4 flex items-start gap-6">
                <span className="font-display text-white/30 text-sm font-bold pt-1">0{i + 1}</span>
                <span className="text-white text-lg leading-snug">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
